import { useLocation } from "react-router-dom";
import EmptyCard from "../../components/EmptyCard";
import { extractSearchProductsV2 } from "../../helpers/extract";
import Card from "../../components/Card";
import { API, graphqlOperation } from "aws-amplify";
import { searchProducts as searchProductsQuery } from "../../graphql/queries";
import { buildProductSearchVars } from "../../utils/buildSearchVars";
import wrapPromise from "../../helpers/wrapPromise";
import SearchBar from "../../components/SearchBar";
import { useState, useRef, Suspense } from "react";
import Waiting from "../../components/Waiting";
import { TbArrowBigUpLines, TbArrowBigDownLines } from "react-icons/tb";
import Footer from "../../components/Footer";

const PAGE_SIZE = 50;

function Search() {
  const topRef = useRef<any>(null);
  const bottomRef = useRef<any>(null);
  const [searchResultProducts, setSearchResultProducts] = useState<any>([]);
  const [pageFrom, setPageFrom] = useState<number>(0);
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const q: any = params.get("q") ? params.get("q") : "";
  const scope: any = params.get("scope") ? params.get("scope") : "ALL";
  const status: any = params.get("status") ? params.get("status") : "ALL";
  const startSearchUpdatedDate: any = params.get("updated_start")
    ? params.get("updated_start")
    : undefined;
  const endSearchUpdatedDate: any = params.get("updated_end")
    ? params.get("updated_end")
    : undefined;

  const searchProducts = () => {
    const vars = buildProductSearchVars({
      q,
      scope,
      status,
      limit: PAGE_SIZE,
      from: pageFrom,
      startUpdatedDate: startSearchUpdatedDate,
      endUpdatedDate: endSearchUpdatedDate,
    });

    const result = API.graphql(graphqlOperation(searchProductsQuery, vars));

    return {
      result: wrapPromise(result),
    };
  };

  const sr = searchProducts();

  const UpdateSearchResult = ({ sr }: any) => {
    if (!sr) {
      return <EmptyCard message="NO SEARCH RESULT" />;
    }
    const searchResults = sr.result.read();

    const products = extractSearchProductsV2(searchResults);
    const total = searchResults.data.searchProducts.total || 0;
    const loadedCount = searchResultProducts.length + products.length;
    const hasMore = products.length === PAGE_SIZE && loadedCount < total;

    if (searchResultProducts.length === 0 && products.length === 0) {
      return <EmptyCard message="RESULT NOT FOUND!" />;
    }

    return (
      <div>
        <div className={`xs:grid xs:grid-cols-1 sm:flex sm:flex-wrap sm:justify-center sm:gap-4 sm:p-4`}>
          {[...searchResultProducts, ...products].map((sr: any) => {
            return (
              <div key={sr.value.id} className="my-2 lg:mx-2">
                <Card data={sr.value} />
              </div>
            );
          })}
        </div>
        {hasMore ? (
          <div className="flex flex-row justify-center gap-2 m-4">
            <button
              type="button"
              className="inline-block rounded bg-primary-100 px-6 pb-2 pt-2.5 text-xs font-medium uppercase leading-normal text-primary-700 transition duration-150 ease-in-out hover:bg-primary-accent-100 focus:bg-primary-accent-100 focus:outline-none focus:ring-0 active:bg-primary-accent-200"
              onClick={() => {
                setSearchResultProducts([...searchResultProducts, ...products]);
                setPageFrom(pageFrom + PAGE_SIZE);
              }}
            >
              Load More{" "}
              <span className="font-bold">
                [
                <span className="ml-2 inline-block whitespace-nowrap rounded-[0.27rem] bg-danger-100 px-[0.65em] pb-[0.25em] pt-[0.35em] text-center align-baseline text-[1.5em] font-bold leading-none text-danger-700">
                  {[...searchResultProducts, ...products].length} / {total}
                </span>{" "}
                products ]
              </span>
            </button>
          </div>
        ) : (
          <div className="flex flex-row justify-center p-2">
            <span className="font-bold uppercase">
              <span className="ml-2 inline-block whitespace-nowrap rounded-[0.27rem] bg-danger-100 px-[0.65em] pb-[0.25em] pt-[0.35em] text-center align-baseline text-[1.5em] font-bold leading-none text-danger-700">
                {[...searchResultProducts, ...products].length} / {total}
              </span>{" "}
              products
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div ref={topRef} />
      <div className="flex flex-row justify-center">
        <div className="w-full p-4 lg:w-3/4">
          <SearchBar
            brand={scope}
            searchInput={q}
            status={status}
            enableUpdatedDateSearch={
              startSearchUpdatedDate && endSearchUpdatedDate ? true : false
            }
            startSearchUpdatedDate={
              startSearchUpdatedDate
                ? new Date(startSearchUpdatedDate)
                : undefined
            }
            endSearchUpdatedDate={
              endSearchUpdatedDate ? new Date(endSearchUpdatedDate) : undefined
            }
          />
        </div>
      </div>
      <div className="h-screen overflow-auto">
        <div className="flex flex-row justify-center">
          <button
            className="inline-block rounded-full bg-primary px-6 pb-2 pt-2.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-primary-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-primary-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-primary-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(59,113,202,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)]"
            onClick={() => {
              bottomRef.current?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            <span className="flex flex-row justify-center gap-2">
              <TbArrowBigDownLines size={15} /> go to bottom
            </span>
          </button>
        </div>

        <Suspense fallback={<Waiting />}>
          <UpdateSearchResult sr={sr} />
        </Suspense>
        {searchResultProducts.length > 10 && (
          <div className="flex flex-row justify-center py-2">
            <button
              className="inline-block rounded-full bg-info px-6 pb-2 pt-2.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#54b4d3] transition duration-150 ease-in-out hover:bg-info-600 hover:shadow-[0_8px_9px_-4px_rgba(84,180,211,0.3),0_4px_18px_0_rgba(84,180,211,0.2)] focus:bg-info-600 focus:shadow-[0_8px_9px_-4px_rgba(84,180,211,0.3),0_4px_18px_0_rgba(84,180,211,0.2)] focus:outline-none focus:ring-0 active:bg-info-700 active:shadow-[0_8px_9px_-4px_rgba(84,180,211,0.3),0_4px_18px_0_rgba(84,180,211,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(84,180,211,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(84,180,211,0.2),0_4px_18px_0_rgba(84,180,211,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(84,180,211,0.2),0_4px_18px_0_rgba(84,180,211,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(84,180,211,0.2),0_4px_18px_0_rgba(84,180,211,0.1)]"
              onClick={() => {
                topRef.current?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <span className="flex flex-row justify-center gap-2">
                <TbArrowBigUpLines size={15} /> go to top
              </span>
            </button>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      <Footer/>
    </>
  );
}
export default Search;
